<template>
  <div class="mta-mdetail">
    <div class="mta-mdetail__head">
      <h3>{{ product.name }}</h3>
      <span class="mta-mdetail__range">{{ rangeLabel }}</span>
      <span class="mta-spacer"></span>
      <button type="button" class="mta-btn mta-btn--secondary mta-btn--sm" @click.stop="$emit('export')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 21h16"/></svg>
        Export CSV
      </button>
    </div>
    <div v-if="product.note" class="mta-mdetail__note">{{ product.note }}</div>

    <!-- White Glove customer funnel (all time) -->
    <div v-if="product.funnel && funnel" class="mta-wgf">
      <div class="mta-wgf__head">
        <span class="mta-eyebrow">Customer funnel · all time</span>
        <span class="mta-muted" style="font-size:12px">
          {{ funnel.users }} users · {{ funnel.ordered }} ordered something ({{ pctOf(funnel.ordered) }}) ·
          {{ funnel.noOrder }} no order ({{ pctOf(funnel.noOrder) }})
        </span>
      </div>
      <div v-for="row in funnelRows" :key="row.label" class="mta-frow">
        <span class="mta-fl">{{ row.label }}</span>
        <div class="mta-fbar"><i :style="{ width: Math.min(100, row.pct) + '%', background: row.color }"></i></div>
        <span class="mta-fv">{{ row.value }}</span>
        <span class="mta-fp">{{ row.pct.toFixed(1) }}%</span>
        <span class="mta-fr">{{ row.note }}</span>
      </div>
      <div class="mta-muted" style="font-size:11.5px;margin-top:6px">Bars are % of all {{ funnel.users }} White Glove users.</div>
    </div>

    <!-- monthly bars of the headline metric -->
    <div class="mta-bchart">
      <div class="mta-bchart__head">
        <span class="mta-eyebrow">{{ headDef.label }} by month</span>
        <span class="mta-bchart__leg"><i class="mta-cur"></i> This period <i class="mta-pv"></i> Same month last year</span>
      </div>
      <div class="mta-bchart__plot">
        <div v-for="bar in chart.bars" :key="bar.key" class="mta-bchart__bar"
          :class="{ 'mta-bchart__bar--prev': bar.cls === 'prev', 'mta-bchart__bar--dim': bar.cls === 'dim' }"
          :title="bar.title"
          :style="{ left: bar.left + '%', width: bar.width + '%', height: bar.height + '%' }"></div>
      </div>
      <div class="mta-bchart__x">
        <span v-for="label in chart.labels" :key="label.key" :style="{ left: label.left + '%' }">{{ label.text }}</span>
      </div>
    </div>

    <!-- by Customer Manager -->
    <div v-if="product.cm && managers.length" class="mta-cmwrap">
      <div class="mta-cm-head">
        <span class="mta-eyebrow">By Customer Manager · {{ rangeLabel }}</span>
        <span class="mta-muted" style="font-size:11.5px">Chip = change vs previous period. Columns sum to All CMs. Filter highlights a column.</span>
        <span class="mta-spacer"></span>
        <div class="mta-seg">
          <button type="button" :class="{ 'mta-active': cmFilter === null }" @click.stop="$emit('cm-filter', null)">All</button>
          <button v-for="manager in managers" :key="manager.key" type="button"
            :class="{ 'mta-active': cmFilter === manager.key }" @click.stop="$emit('cm-filter', manager.key)">{{ manager.name }}</button>
        </div>
      </div>
      <table class="mta-tbl">
        <thead>
          <tr>
            <th>Metric</th>
            <th v-for="manager in managers" :key="manager.key" class="mta-r" :class="{ 'mta-cmon': cmFilter === manager.key }">{{ manager.name }}</th>
            <th class="mta-r">All CMs</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in cmRows" :key="row.id">
            <td>{{ row.label }} <span v-if="row.snapshot" class="mta-snaptag">snapshot</span></td>
            <td v-for="cell in row.cells" :key="cell.key" class="mta-r mta-num" :class="{ 'mta-cmon': cmFilter === cell.key }">
              {{ cell.value }}
              <small><delta-chip :chip="cell.chip" /></small>
            </td>
            <td class="mta-r mta-num">{{ row.all }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- the metrics of the product -->
    <table class="mta-tbl">
      <thead>
        <tr>
          <th>Metric</th><th class="mta-r">Current</th><th class="mta-r">Prev period</th><th class="mta-r"><span class="mta-delta">Δ</span> prev</th>
          <th class="mta-r">Same period LY</th><th class="mta-r"><span class="mta-delta">Δ</span> year</th><th class="mta-r">12-mo trend</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.id" :class="{ 'mta-totrow': row.total }">
          <td>{{ row.label }} <span v-if="row.snapshot" class="mta-snaptag">snapshot</span><span v-if="row.hint" class="mta-hint" :title="row.hint">?</span></td>
          <td class="mta-r mta-num">{{ row.cur }}</td>
          <td class="mta-r mta-muted">{{ row.prev }}</td>
          <td class="mta-r"><delta-chip :chip="row.prevChip" /></td>
          <td class="mta-r mta-muted">{{ row.yoy }}</td>
          <td class="mta-r"><delta-chip :chip="row.yearChip" /></td>
          <td class="mta-r"><sparkline :points="row.spark" :width="110" :height="26" /></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import DeltaChip from './DeltaChip.vue'
import Sparkline from './Sparkline.vue'
import { barChart, calc, chip, cmWinVal, deltaChip, fmt, metricSeries, pct, ppChip, sparkline, yearChip } from './metricsCalc'

/** The expanded breakdown of a product card (.mdetail): chart, Customer Manager split, metric table. */
export default {
  name: 'MetricsProductDetail',
  components: { DeltaChip, Sparkline },
  props: {
    product: { type: Object, required: true },
    data: { type: Object, required: true },       // the series payload
    defs: { type: Object, required: true },       // METRICS
    s: { type: Number, required: true },
    e: { type: Number, required: true },
    rangeLabel: { type: String, default: '' },
    cmFilter: { type: String, default: null },
    funnel: { type: Object, default: null },
  },
  computed: {
    headDef () {
      return this.defs[this.product.head]
    },
    chart () {
      return barChart(this.data, this.headDef, this.s, this.e)
    },
    managers () {
      return (this.data.cm && this.data.cm.managers) || []
    },
    metricIds () {
      return this.product.metrics.concat(this.product.total ? [this.product.total] : [])
    },
    rows () {
      return this.metricIds.map((id) => {
        const def = this.defs[id]
        const c = calc(this.data, this.defs, def, this.s, this.e)
        return {
          id,
          label: def.label,
          snapshot: def.agg === 'last',
          hint: def.hint || '',
          total: id === this.product.total,
          cur: fmt(c.cur, def.fmt),
          prev: fmt(c.prev, def.fmt),
          yoy: fmt(c.yoy, def.fmt),
          prevChip: deltaChip(c, ''),
          yearChip: yearChip(c, ''),
          spark: sparkline(metricSeries(this.data, this.defs, def), this.data.months.length - 1, 110, 26),
        }
      })
    },
    cmRows () {
      const len = this.e - this.s + 1
      return this.product.metrics.map((id) => {
        const def = this.defs[id]
        const all = calc(this.data, this.defs, def, this.s, this.e)
        const cells = this.managers.map((manager) => {
          const cur = cmWinVal(this.data, this.defs, def, manager.key, this.s, this.e)
          const prev = cmWinVal(this.data, this.defs, def, manager.key, this.s - len, this.e - len)
          return {
            key: manager.key,
            value: cur === null && def.agg === 'ltv' ? 'n/a' : fmt(cur, def.fmt),
            chip: def.fmt === 'pct' ? ppChip(cur, prev, '') : chip(pct(cur, prev), def.goodDown, ''),
          }
        })
        return { id, label: def.label, snapshot: def.agg === 'last', cells, all: fmt(all.cur, def.fmt) }
      })
    },
    funnelRows () {
      const f = this.funnel
      if (!f) return []
      const users = (n) => n + ' user' + (n === 1 ? '' : 's')
      const money = (v) => '$' + Math.round(v || 0).toLocaleString('en-US')
      const share = (n, of) => (of ? (n / of) * 100 : 0)
      return [
        { label: 'Ordered an audit', value: users(f.auditBuyers), pct: share(f.auditBuyers, f.users), color: '#FF5700',
          note: money(f.auditRevenue) + ' · ' + f.auditOrders + ' orders · ' + (f.auditBuyers ? (f.auditOrders / f.auditBuyers).toFixed(2) : '0') + ' avg' },
        { label: 'Audit only (no further purchase)', value: users(f.auditOnly), pct: share(f.auditOnly, f.users), color: '#FFB95A',
          note: share(f.auditOnly, f.auditBuyers).toFixed(1) + '% of audit buyers' },
        { label: 'Multi-audit', value: users(f.multiAudit), pct: share(f.multiAudit, f.users), color: '#FFB95A', note: '' },
        { label: 'SILO after audit', value: users(f.siloAfterAudit), pct: share(f.siloAfterAudit, f.users), color: '#25A97A',
          note: money(f.siloRevenue) + ' · attach ' + share(f.siloAfterAudit, f.auditBuyers).toFixed(1) + '% of audit buyers' },
        { label: 'Multi-SILO', value: users(f.multiSilo), pct: share(f.multiSilo, f.users), color: '#25A97A', note: '' },
        { label: 'Content only', value: users(f.contentOnly), pct: share(f.contentOnly, f.users), color: '#316BFF',
          note: money(f.contentRevenue) },
      ]
    },
  },
  methods: {
    pctOf (n) {
      const users = this.funnel ? this.funnel.users : 0
      return (users ? ((n / users) * 100).toFixed(1) : '0.0') + '%'
    },
  },
}
</script>
